/**
 * DayTotalsBar — compact kcal + protein bar showing consumed vs target.
 *
 * Updates optimistically from the echoed DaySummary on every mutation
 * (ADR-0003 §4, FR-4.3). When no target exists, shows consumed only.
 * Color is never used alone: bars have text labels (WCAG 2.1 AA).
 */
import type { components } from '@nutians/api-client';

type DaySummary = components['schemas']['DaySummary'];

interface DayTotalsBarProps {
  day: DaySummary;
  className?: string;
}

interface MacroBarProps {
  label: string;
  consumed: number;
  target: number | null;
  unit: string;
  colorClass: string;
}

function MacroBar({ label, consumed, target, unit, colorClass }: MacroBarProps) {
  const pct = target && target > 0 ? Math.min(100, (consumed / target) * 100) : null;

  return (
    // biome-ignore lint/a11y/useSemanticElements: fieldset resets flex styling; div+role="group" is intentional
    <div
      className="flex flex-col gap-1"
      role="group"
      aria-label={`${label}: ${Math.round(consumed)}${unit} consumed`}
    >
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-ink dark:text-ink">{label}</span>
        <span className="text-ink-soft dark:text-ink-soft">
          {Math.round(consumed)}
          {unit}
          {target !== null && (
            <span>
              {' '}
              / {Math.round(target)}
              {unit}
            </span>
          )}
        </span>
      </div>
      {pct !== null && (
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-husk dark:bg-shell"
          role="progressbar"
          aria-valuenow={Math.round(consumed)}
          aria-valuemin={0}
          aria-valuemax={target ?? 0}
          aria-label={`${label} progress: ${Math.round(pct)}%`}
        >
          <div
            className={`h-full rounded-full transition-all duration-300 ${colorClass}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

export function DayTotalsBar({ day, className = '' }: DayTotalsBarProps) {
  const { consumed, target } = day;

  return (
    <section
      className={`rounded-lg border border-husk bg-shell px-4 py-3 dark:border-ink/20 dark:bg-leaf ${className}`}
      aria-label={`Daily totals for ${day.date}`}
    >
      <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-ink-soft dark:text-ink-soft">
        Today
      </h3>
      <div className="flex flex-col gap-3">
        <MacroBar
          label="Calories"
          consumed={consumed.kcal}
          target={target?.kcal ?? null}
          unit=" kcal"
          colorClass="bg-shell dark:bg-husk"
        />
        <MacroBar
          label="Protein"
          consumed={consumed.proteinG}
          target={target?.proteinG ?? null}
          unit="g"
          colorClass="bg-mist/100"
        />
      </div>

      {/* Remaining hint when target is set */}
      {day.remaining !== null && (
        <p className="mt-3 text-xs text-ink-soft dark:text-ink-soft" aria-live="polite">
          {day.remaining.kcal >= 0
            ? `${Math.round(day.remaining.kcal)} kcal remaining`
            : `${Math.abs(Math.round(day.remaining.kcal))} kcal over target`}
        </p>
      )}
    </section>
  );
}
